<script setup lang="ts">
import { useRouter } from 'vue-router';
import {
  FhCard,
  FhDropdownItem,
  FhDropdownMenu,
  FhDropdownSection,
  FhIcon,
  FhIconButton,
  FhSpinner,
  FhText,
} from '@fareharbor-com/beacon-vue';
import { option, photos } from '@fareharbor-com/beacon-vue/icons';
import ItemListAvailability from '@/components/itemList/ItemListAvailability.vue';
import ItemListBookmark from '@/components/itemList/ItemListBookmark.vue';
import ItemListCompany from '@/components/itemList/ItemListCompany.vue';
import ItemListDetails from '@/components/itemList/ItemListDetails.vue';
import ItemListLocation from '@/components/itemList/ItemListLocation.vue';
import ItemListRating from '@/components/itemList/ItemListRating.vue';
import type { ItemListTableInstance } from '@/components/itemList/types';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';

interface ItemListCardsProps {
  data: ItemListTableInstance[];
  isLoading: boolean;
}

const router = useRouter();

const redirectToItemDetails = (itemId: string) => {
  router.push(`/items/${itemId}`);
};

const formattedItemTypes = (item: ItemListTableInstance) => {
  const formatString = (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1);
  return item.itemType.map((type: any) => formatString(type.name)).join(' • ');
};

const props = defineProps<ItemListCardsProps>();
</script>

<template>
  <div class="cards-container flex-1 overflow-y-scroll overflow-x-hidden mt-4">
    <BaseFlexbox
      v-if="props.isLoading"
      justifyContent="center"
      alignItems="center"
      class="h-full"
    >
      <FhSpinner />
    </BaseFlexbox>
    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      data-test-id="item-list-cards"
    >
      <FhCard
        v-for="item in props.data"
        :key="item.itemId"
        data-test-id="item-card"
        @click="redirectToItemDetails(item.itemId.toString())"
      >
        <!-- Image Section with overlays -->
        <div class="relative">
          <div
            class="w-full h-40 overflow-hidden rounded-t-lg bg-gray-100 flex items-center justify-center"
          >
            <!-- Custom image display for full coverage -->
            <img
              v-if="item.imageUrl && item.imageUrl !== ''"
              :src="item.imageUrl"
              :alt="item.name"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <!-- Fallback for no image -->
            <FhIcon
              v-else
              :icon="photos"
              color="disabled"
              size="lg"
              class="text-gray-400"
            />
          </div>

          <!-- Bottom overlay: Availability on left, Image count on right -->
          <div
            class="absolute bottom-0 left-0 right-0 flex items-center justify-between px-2"
          >
            <!-- Availability on the left -->
            <div data-test-id="availability-details">
              <ItemListAvailability :row="item" />
            </div>

            <!-- Extra images count on the right -->
            <div
              v-if="item.imageUrl && item.extraImagesCount > 0"
              class="bg-black/80 text-white px-2 py-1 rounded-md text-xs font-medium shadow-lg"
            >
              +{{ item.extraImagesCount }}
            </div>
          </div>
        </div>

        <!-- Content Section -->
        <div class="p-2 flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <FhText variant="body-sm" class="text-gray-500">
              {{ formattedItemTypes(item) }}
            </FhText>

            <div class="flex items-center gap-2" @click.stop>
              <FhDropdownMenu>
                <FhIconButton variant="plain" size="sm" :icon="option">
                  View
                </FhIconButton>
                <template #sections>
                  <FhDropdownSection>
                    <FhDropdownItem
                      @click="redirectToItemDetails(item.itemId.toString())"
                    >
                      <FhText variant="body-sm">Preview Details</FhText>
                    </FhDropdownItem>
                  </FhDropdownSection>
                </template>
              </FhDropdownMenu>

              <ItemListBookmark :row="item" />
            </div>
          </div>

          <ItemListDetails
            :row="item"
            class="border-b border-gray-100 !pb-3 !pl-0 !pr-0"
          />

          <!-- Company -->
          <ItemListCompany :row="item" class="!p-0" />

          <!-- Location -->
          <ItemListLocation :row="item" class="!p-0" />

          <!-- Rating and Quality Score on same line -->
          <div class="flex items-center justify-between">
            <ItemListRating :row="item" />
            <div
              data-test-id="quality-score-details"
              class="flex flex-col items-end"
            >
              <div class="flex items-center gap-1">
                <div class="text-gray-800 text-sm">{{ item.qualityScore }}</div>
                <div class="text-gray-500 text-sm">/100</div>
              </div>
              <div class="text-gray-500 text-sm">Quality Score</div>
            </div>
          </div>
        </div>
      </FhCard>
    </div>
  </div>
</template>
